import { Tooltip } from "@design-system/Tooltip/Tooltip";
import classNames from "classnames";
import { TransactionsImportStatus } from "@common/types/financialDocument";
import { useContext } from "react";
import { isImporterTransactionsQueue } from "@common/helpers/financialDocument";
import { pluralize } from "@common/helpers/string";
import { Loader } from "@design-system/Loader/Loader";
import { AlertCircleIcon, CheckIcon } from "@icons";
import {
    documentBeingProcessed,
    getDisplayedErrors,
    getDisplayErrorMessage,
} from "./helpers";
import { ImporterDocumentRow } from "./types";
import importerStyles from "./ImporterPage.module.scss";
import { ImporterDocumentStatusMessage } from "./ImporterDocumentStatusMessage";
import { ImporterContext } from "./importer.context";

interface Props {
    documentRow: ImporterDocumentRow;
    onlyIcon?: boolean;
}

export const ImporterDocumentStatus: React.FC<Props> = ({
    documentRow,
    onlyIcon,
}) => {
    const {
        readyToImportLabel,
        setPreviewedRowId,
        importerQueue,
        reconciliedFinancialAccount,
    } = useContext(ImporterContext);

    const { financialDocument } = documentRow;

    if (!financialDocument) {
        return (
            <ImporterDocumentStatusMessage
                icon={<Loader inline />}
                message="Uploading"
                onlyIcon={onlyIcon}
            />
        );
    }

    if (documentBeingProcessed(financialDocument)) {
        return (
            <ImporterDocumentStatusMessage
                icon={<Loader inline />}
                message="Processing"
                onlyIcon={onlyIcon}
            />
        );
    }

    if (
        financialDocument.transactionsImportStatus ===
            TransactionsImportStatus.FINISHED &&
        isImporterTransactionsQueue(importerQueue)
    ) {
        return (
            <ImporterDocumentStatusMessage
                icon={<CheckIcon className="icon-color-blue-600" />}
                message="Imported"
                className="text-blue-600"
                onlyIcon={onlyIcon}
            />
        );
    }

    if (
        financialDocument.transactionsImportStatus ===
        TransactionsImportStatus.SCHEDULED
    ) {
        return (
            <ImporterDocumentStatusMessage
                icon={<Loader inline />}
                message="Import scheduled"
                onlyIcon={onlyIcon}
            />
        );
    }

    if (
        financialDocument.transactionsImportStatus ===
        TransactionsImportStatus.IN_PROGRESS
    ) {
        return (
            <ImporterDocumentStatusMessage
                icon={<Loader inline />}
                message="Importing"
                onlyIcon={onlyIcon}
            />
        );
    }

    if (
        financialDocument.transactionsImportStatus ===
        TransactionsImportStatus.FAILED
    ) {
        return (
            <ImporterDocumentStatusMessage
                icon={<AlertCircleIcon className="icon-color-gray-900" />}
                message="Ready to import (last import failed!)"
                className="text-gray-900"
                onlyIcon={onlyIcon}
            />
        );
    }

    const errors = getDisplayedErrors(
        financialDocument,
        reconciliedFinancialAccount,
    );

    if (errors.length === 1 && !onlyIcon) {
        const errorMessage = getDisplayErrorMessage(
            errors[0],
            financialDocument,
        );
        return (
            <div
                className={classNames(
                    importerStyles.statusMessage,
                    "text-yellow-600",
                    "cursor-pointer",
                )}
                onClick={() => setPreviewedRowId(documentRow.id)}
            >
                <AlertCircleIcon className="icon-color-yellow-600" />
                {errorMessage}
            </div>
        );
    }

    if (errors.length > 1 || (errors.length === 1 && onlyIcon)) {
        return (
            <Tooltip
                bullets={errors.map((error) =>
                    getDisplayErrorMessage(error, financialDocument),
                )}
            >
                <div
                    className={classNames(
                        importerStyles.statusMessage,
                        "text-yellow-600",
                        "cursor-pointer",
                    )}
                    onClick={() => setPreviewedRowId(documentRow.id)}
                >
                    <AlertCircleIcon className="icon-color-yellow-600" />
                    {!onlyIcon &&
                        `${errors.length} ${pluralize("review task", errors.length)}`}
                </div>
            </Tooltip>
        );
    }

    return (
        <ImporterDocumentStatusMessage
            icon={<CheckIcon className="icon-color-gray-900" />}
            message={readyToImportLabel}
            className="text-gray-900"
            onlyIcon={onlyIcon}
        />
    );
};
